/**
 * Type definitions for the Professional module
 * Based on backend ProfessionalValidator and ProfessionalService
 */

/**
 * Professional types (based on ProfessionalType enum from backend)
 */
export enum ProfessionalType {
  MEDICO_TRABALHO = 'MEDICO_TRABALHO',
  ENFERMEIRO = 'ENFERMEIRO',
  TECNICO_ENFERMAGEM = 'TECNICO_ENFERMAGEM',
  ENGENHEIRO_SEGURANCA = 'ENGENHEIRO_SEGURANCA',
  ENGENHEIRO_ELETRICISTA = 'ENGENHEIRO_ELETRICISTA',
  ENGENHEIRO_MECANICO = 'ENGENHEIRO_MECANICO',
  TECNICO_SEGURANCA = 'TECNICO_SEGURANCA',
  FONOAUDIOLOGO = 'FONOAUDIOLOGO',
  PSICOLOGO = 'PSICOLOGO',
  MEDICO_EXAMINADOR = 'MEDICO_EXAMINADOR',
  ERGONOMISTA = 'ERGONOMISTA',
  OUTRO = 'OUTRO',
}

/**
 * Tipos de profissional que são engenheiros: registro no CREA.
 */
export const ENGINEER_PROFESSIONAL_TYPES: readonly ProfessionalType[] = [
  ProfessionalType.ENGENHEIRO_SEGURANCA,
  ProfessionalType.ENGENHEIRO_ELETRICISTA,
  ProfessionalType.ENGENHEIRO_MECANICO,
];

/**
 * Indica se o tipo é de engenheiro — usado para exibir e exigir os campos de CREA.
 */
export const isEngineerProfessionalType = (type?: string): boolean =>
  ENGINEER_PROFESSIONAL_TYPES.includes(type as ProfessionalType);

/**
 * As chaves de registro em conselho guardadas no `meta` do profissional.
 *
 * MTB é registro ministerial, não conselho, mas entra na mesma lista porque
 * ocupa o mesmo papel: é o número que sai impresso no documento do técnico de
 * segurança.
 */
export type ConselhoKey =
  | 'crm'
  | 'crea'
  | 'crfa'
  | 'crefito'
  | 'crq'
  | 'crp'
  | 'coren'
  | 'cro'
  | 'cra'
  | 'mtb';

export interface ConselhoProfissional {
  chave: ConselhoKey;
  rotulo: string;
}

/**
 * Os conselhos, na ORDEM DE FALLBACK do backend
 * (`ProfessionalRegistrationResolver.ORDEM_FALLBACK`).
 *
 * A ordem é contrato, não estética: é ela que decide qual conselho a tela
 * mostra quando o profissional tem mais de um, e o backend usa a mesma para
 * decidir qual sai impresso no documento. Divergir daqui faz a listagem
 * discordar do laudo.
 *
 * O rótulo acompanha o do backend (`ROTULO_POR_CHAVE`), com uma exceção
 * proposital: MTB, que lá sai como "MTE" no documento e aqui continua "MTB",
 * o nome do campo no cadastro.
 */
export const CONSELHOS_PROFISSIONAIS: readonly ConselhoProfissional[] = [
  { chave: 'crm', rotulo: 'CRM' },
  { chave: 'crea', rotulo: 'CREA' },
  { chave: 'crfa', rotulo: 'CRFa' },
  { chave: 'crefito', rotulo: 'CREFITO' },
  { chave: 'crq', rotulo: 'CRQ' },
  { chave: 'crp', rotulo: 'CRP' },
  { chave: 'coren', rotulo: 'COREN' },
  { chave: 'cro', rotulo: 'CRO' },
  { chave: 'cra', rotulo: 'CRA' },
  { chave: 'mtb', rotulo: 'MTB' },
];

/**
 * O conselho de cada tipo de profissional — espelha o `CHAVE_POR_TIPO` do
 * `ProfessionalRegistrationResolver` do backend.
 *
 * `OUTRO` não aparece de propósito: é o tipo de quem não tem conselho fixo.
 */
export const CONSELHO_POR_TIPO: Partial<Record<ProfessionalType, ConselhoKey>> = {
  [ProfessionalType.MEDICO_TRABALHO]: 'crm',
  [ProfessionalType.MEDICO_EXAMINADOR]: 'crm',
  [ProfessionalType.ENGENHEIRO_SEGURANCA]: 'crea',
  [ProfessionalType.ENGENHEIRO_ELETRICISTA]: 'crea',
  [ProfessionalType.ENGENHEIRO_MECANICO]: 'crea',
  [ProfessionalType.TECNICO_SEGURANCA]: 'mtb',
  [ProfessionalType.FONOAUDIOLOGO]: 'crfa',
  [ProfessionalType.PSICOLOGO]: 'crp',
  [ProfessionalType.ENFERMEIRO]: 'coren',
  [ProfessionalType.TECNICO_ENFERMAGEM]: 'coren',
  [ProfessionalType.ERGONOMISTA]: 'crefito',
};

/** O conselho correspondente ao tipo, ou `undefined` quando o tipo não tem um. */
export const conselhoDoTipo = (type?: string): ConselhoProfissional | undefined => {
  const chave = type ? CONSELHO_POR_TIPO[type as ProfessionalType] : undefined;
  return chave ? CONSELHOS_PROFISSIONAIS.find((conselho) => conselho.chave === chave) : undefined;
};

/**
 * Professional status (UserStatus enum from backend)
 */
export enum ProfessionalStatus {
  ATIVO = 'Ativo',
  INATIVO = 'Inativo',
  SUSPENSO = 'Suspenso',
  BLOQUEADO = 'Bloqueado',
  PENDENTE = 'Pendente',
}

/**
 * Professional metadata interface
 */
export interface ProfessionalMeta {
  // Professional registrations
  professionalType?: ProfessionalType;
  crm?: string;
  rqe?: string;
  crea?: string;
  cro?: string;
  cra?: string;
  mtb?: string;
  crfa?: string;
  crefito?: string;
  crq?: string;
  crp?: string;
  coren?: string;
  conselhoUf?: string;
  specialty?: string;
  registrationNumber?: string;
  certification?: string;

  // Responsibilities
  isExaminer?: boolean | string;
  isCoordinator?: boolean | string;
  isMedicalResponsible?: boolean | string;
  isTechnicalResponsible?: boolean | string;
  isEnvironmentalResponsible?: boolean | string;
  isLegalResponsible?: boolean | string;
  
  // Bank data
  bankName?: string;
  bankAgency?: string;
  bankAccount?: string;
  bankAccountType?: 'corrente' | 'poupanca';
  pixKeyType?: 'cpf' | 'email' | 'telefone' | 'aleatoria';
  pixKey?: string;
  pis?: string;
  
  // Company data (for autonomous professionals)
  companyName?: string;
  companyCnpj?: string;
  
  // Appointment settings
  defaultAppointmentDuration?: number | string;

  // Other fields
  digitalSignature?: string;
  observations?: string;
}

/**
 * Main Professional interface
 */
export interface Professional {
  id: number | string;
  name: string;
  email: string;
  cpf: string;
  whatsapp?: string;
  avatarPath?: string;
  situacao: string;
  userType: string;
  meta: ProfessionalMeta;
  createdAt: string | Date;
  updatedAt: string | Date;
}

/**
 * Professional filters for listing
 */
export interface ProfessionalFilters {
  search?: string;
  status?: string;
  professionalType?: string;
  isExaminer?: boolean;
  isCoordinator?: boolean;
}

/**
 * Paginated professionals response
 */
export interface PaginatedProfessionalsResponse {
  message: string;
  data: Professional[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Create professional request
 */
export interface CreateProfessionalRequest {
  // Required fields
  name: string;
  email: string;
  cpf: string;
  password: string;
  
  // Optional User fields
  whatsapp?: string;
  avatarPath?: string;
  situacao?: string;
  
  // Professional type and registrations (can be flat or nested in meta)
  professionalType?: string;
  crm?: string;
  rqe?: string;
  crea?: string;
  cro?: string;
  cra?: string;
  mtb?: string;
  crfa?: string;
  crefito?: string;
  crq?: string;
  crp?: string;
  coren?: string;
  conselhoUf?: string;
  specialty?: string;
  registrationNumber?: string;
  certification?: string;

  // Responsibilities
  isExaminer?: boolean;
  isCoordinator?: boolean;
  isMedicalResponsible?: boolean;
  isTechnicalResponsible?: boolean;
  isEnvironmentalResponsible?: boolean;
  isLegalResponsible?: boolean;
  
  // Bank data
  bankName?: string;
  bankAgency?: string;
  bankAccount?: string;
  bankAccountType?: string;
  pixKeyType?: string;
  pixKey?: string;
  pis?: string;
  
  // Company data
  companyName?: string;
  companyCnpj?: string;
  
  // Appointment settings
  defaultAppointmentDuration?: number;

  // Other
  digitalSignature?: string;
  observations?: string;

  // Provider bindings (clinics)
  providerBindings?: Array<{ providerId: number; providerName?: string; function: string }>;

  // Nested meta (alternative structure)
  meta?: Partial<ProfessionalMeta>;
}

/**
 * Update professional request
 */
export type UpdateProfessionalRequest = Partial<Omit<CreateProfessionalRequest, 'cpf'>>;

/**
 * Professional type labels for display
 */
export const PROFESSIONAL_TYPE_LABELS: Record<ProfessionalType, string> = {
  [ProfessionalType.MEDICO_TRABALHO]: 'Médico do Trabalho',
  [ProfessionalType.ENFERMEIRO]: 'Enfermeiro',
  [ProfessionalType.TECNICO_ENFERMAGEM]: 'Técnico de Enfermagem',
  [ProfessionalType.ENGENHEIRO_SEGURANCA]: 'Engenheiro de Segurança',
  [ProfessionalType.ENGENHEIRO_ELETRICISTA]: 'Engenheiro Eletricista',
  [ProfessionalType.ENGENHEIRO_MECANICO]: 'Engenheiro Mecânico',
  [ProfessionalType.TECNICO_SEGURANCA]: 'Técnico de Segurança',
  [ProfessionalType.FONOAUDIOLOGO]: 'Fonoaudiólogo',
  [ProfessionalType.PSICOLOGO]: 'Psicólogo',
  [ProfessionalType.MEDICO_EXAMINADOR]: 'Médico Examinador',
  [ProfessionalType.ERGONOMISTA]: 'Ergonomista',
  [ProfessionalType.OUTRO]: 'Outro',
};

/**
 * Professional status labels for display
 */
export const PROFESSIONAL_STATUS_LABELS: Record<string, string> = {
  'Ativo': 'Ativo',
  'Inativo': 'Inativo',
  'Suspenso': 'Suspenso',
  'Bloqueado': 'Bloqueado',
  'Pendente': 'Pendente',
};
